import { Box, Card, Divider } from "@mui/material";
import { BasicStructure, PreOrderData, PreOrderValidationError, SessionData } from "../../common/Interfaces"
import TitleSubtitle from "../common/formControls/titleSubtitle/TitleSubtitle";
import Combobox from "../common/formControls/combobox/Combobox";
import InputTextField from "../common/formControls/textField/InputTextField";
import { getIdentificationTypes } from "../../common/EntitiesManager";
import styles from './ClientData.module.scss';
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { getLocationCities, getLocationStates } from "../../common/requests/LocationRequests";

interface Props {
    auth: SessionData;
    preOrder: PreOrderData;
    setPreOrder: any;
    preOrderValidations: PreOrderValidationError;
    disabled?: boolean;
}

export default function ClientData ({preOrder, setPreOrder, preOrderValidations, auth, disabled}: Props){
    const {t} = useTranslation();
    const [locationStates, setLocationStates] = useState<BasicStructure[]>([]);
    const [locationCities, setLocationCities] = useState<BasicStructure[]>([]);
    const controlsWidth = "100%";
    const preOrderHasId = preOrder?.Id && preOrder.Id > 0;

    useEffect(() => {
        if(!preOrderHasId) obtainsLocationStates();
    }, [preOrderHasId]);

    useEffect(() => {
        if(!preOrderHasId){
            if (preOrder?.State){
                obtainsLocationCities(preOrder.State.Code ?? '');
                return;
            }
    
            setLocationCities([]);
            setPreOrder({
                ...preOrder,
                City: undefined,
            });
        }
    }, [preOrder?.State]);

    const obtainsLocationStates = async () => {
        const states = await getLocationStates(auth.token ?? '', auth.sessionData.ManagementId);
        setLocationStates(states ?? []);
    }

    const obtainsLocationCities = async (parentCode: string) => {
        const cities = await getLocationCities(parentCode, auth.token ?? '', auth.sessionData.ManagementId);
        setLocationCities(cities ?? []);

        setPreOrder({
            ...preOrder,
            City: (cities && cities.length > 0) ? cities[0] : undefined,
        });
    }

    const handleClientDataValueChange = (e: any) => {
        setPreOrder({
            ...preOrder,
            [e.target.name]: e.target.value,
        });
    }

    const handleClientIdentificationTypeChange = (e: any, value: any) => {
        setPreOrder({
            ...preOrder,
            ClientIdentificationTypeData: value,
            ClientIdentificationType: value.Id,
        });
    }

    const handleClientStateChange = (_e: any, value: any) => {
        setPreOrder({
            ...preOrder,
            State: value,
            City: {Id: "0", Name: ''},
        });
    }

    const handleCityStateChange = (_e: any, value: any) => {
        setPreOrder({
            ...preOrder,
            City: value,
        });
    }

    return (
        <Card className={ styles.container}>
            <Box className={styles.verticalContainer}>
                <TitleSubtitle title={t("CLIENTDATA")}/>
                <Divider/>
                <Box className={styles.verticalContainer}>
                    {preOrder.Id === 0 ? (
                        <Combobox fieldId={"ClientIdentificationType"} 
                        disabled={disabled}
                        handleValueChange={handleClientIdentificationTypeChange}
                        fieldValue={preOrder.ClientIdentificationTypeData}
                        fieldOptions={getIdentificationTypes()} 
                        fieldLabel={t("IDENTIFICATIONTYPE")}
                        errorMessage={preOrderValidations.ClientIdentificationType}
                        width={controlsWidth}/>
                    ) : (
                        <InputTextField id={"ClientIdentificationType"} 
                                    label={t("IDENTIFICATIONTYPE")} 
                                    errorMessage={preOrderValidations.ClientIdentificationType}
                                    value={preOrder.ClientIdentificationType ?? ''} 
                                    handleValueChange={undefined}
                                    isReadOnly={preOrder.Id > 0}
                                    width={controlsWidth}/>
                    )}

                    <InputTextField id={"ClientIdentification"} 
                                    label={t("IDENTIFICATION")} 
                                    disabled={preOrder.Id === 0 && disabled}
                                    errorMessage={preOrderValidations.ClientIdentification}
                                    value={preOrder.ClientIdentification} 
                                    handleValueChange={handleClientDataValueChange}
                                    isReadOnly={preOrder.Id > 0}
                                    width={controlsWidth}/>
                                    
                    <InputTextField id={"ClientName"}
                                    label={t("FULLNAME")} 
                                    disabled={preOrder.Id === 0 && disabled}
                                    value={preOrder.ClientName} 
                                    handleValueChange={handleClientDataValueChange}
                                    errorMessage={preOrderValidations.ClientName}
                                    isReadOnly={preOrder.Id > 0}
                                    width={controlsWidth}/>

                    {preOrder.Id === 0 ? (
                        <Combobox fieldId={"State"} 
                        handleValueChange={handleClientStateChange} 
                        disabled={disabled}
                        fieldValue={preOrder.State ?? {Id: "0",Name: ""}} 
                        fieldOptions={locationStates} 
                        fieldLabel={t("DEPARTMENT")}
                        errorMessage={preOrderValidations.ClientState}
                        width={controlsWidth}/>
                    ) : (
                        <InputTextField id={"State"}
                                    label={t("DEPARTMENT")} 
                                    value={preOrder.State?.Name ?? ''} 
                                    handleValueChange={undefined}
                                    errorMessage={preOrderValidations.ClientState}
                                    isReadOnly={preOrder.Id > 0}
                                    width={controlsWidth}/>
                    )}
                    
                    {preOrder.Id === 0 ? (
                        <Combobox fieldId={"City"} 
                            disabled={disabled}
                            handleValueChange={handleCityStateChange}
                            fieldValue={preOrder.City ?? {Id: "0",Name: ""}} 
                            fieldOptions={locationCities} 
                            fieldLabel={t("CITY")}
                            errorMessage={preOrderValidations.ClientCity}
                            width={controlsWidth}/>
                    ) : (
                        <InputTextField id={"City"} 
                                    label={t("CITY")} 
                                    value={preOrder.City?.Name ?? ''} 
                                    handleValueChange={undefined}
                                    errorMessage={preOrderValidations.ClientCity}
                                    isReadOnly={preOrder.Id > 0}
                                    width={controlsWidth}/>
                    )}
                    
                    
                    <InputTextField id={"ClientAddress"} 
                                    label={t("ADDRESS")} 
                                    disabled={preOrder.Id === 0 && disabled}
                                    value={preOrder.ClientAddress} 
                                    handleValueChange={handleClientDataValueChange}
                                    errorMessage={preOrderValidations.ClientAddress}
                                    isReadOnly={preOrder.Id > 0}
                                    width={controlsWidth}/>
                                    
                    <InputTextField id={"ClientPhoneNumber"} 
                                    label={t("PHONENUMBER")} 
                                    disabled={preOrder.Id === 0 && disabled}
                                    value={preOrder.ClientPhoneNumber} 
                                    handleValueChange={handleClientDataValueChange}
                                    errorMessage={preOrderValidations.ClientPhoneNumber}
                                    isReadOnly={preOrder.Id > 0}
                                    width={controlsWidth}/>
                                    
                    <InputTextField id={"ClientEmail"} 
                                    label={t("EMAIL")} 
                                    value={preOrder.ClientEmail ?? ''} 
                                    handleValueChange={handleClientDataValueChange}
                                    errorMessage={preOrderValidations.ClientEmail}
                                    disabled={preOrder.Id === 0 && disabled}
                                    isOptional
                                    isReadOnly={preOrder.Id > 0}
                                    width={controlsWidth}/>
                </Box>
            </Box>
        </Card>
    );
}